import { memo, useCallback } from 'react';
import { Box } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';

import { updateOrgType } from 'redux/ItemPageSlice';
import { setDriver } from 'redux/PlanPageSlice';
import { AppDispatch } from 'utils/GlobalHelpers';

import { usePlanPageUiRevamp } from '../usePlanPageUiRevamp';
import { refetchPlanPageAfterInputPanelClose } from '../planPageFetchHelpers';
import { PLAN_INPUT_PANEL_GAP, PLAN_INPUT_PANEL_PADDING } from '../planInputPanelTheme';
import { PlanRevampProvider } from '../PlanRevampContext';

import DriverPanelHeader from './DriverPanelHeader';
import RevampDesktopPanel from './RevampDesktopPanel';
import RevampDriverTableView from './RevampDriverTableView';

import type { PlanPageRefetchRequest } from '../planPageFetchHelpers';

interface Properties {
  longClickCheck: any;
  isLongClicked: boolean;
  refetchRequest: PlanPageRefetchRequest;
  onInputEdit: PlanPageRefetchRequest;
}

/** Figma 75911:4490 (org chart) / 75916:7557 (table) */
const RevampOrganisationPanel = ({
  longClickCheck,
  isLongClicked,
  refetchRequest,
  onInputEdit,
}: Properties) => {
  const dispatch: AppDispatch = useDispatch();
  const orgChartType = useSelector((state: any) => state.itemPageSlice.orgChartType);
  const isRevampEnabled = usePlanPageUiRevamp();
  const isPropertyView = orgChartType === 2;

  const onUpdateOrgType = useCallback(
    (type: number) => {
      dispatch(updateOrgType(type));
    },
    [dispatch],
  );

  const onClose = useCallback(() => {
    refetchPlanPageAfterInputPanelClose(refetchRequest, { type: 'dimension' });
    dispatch(setDriver(null));
  }, [dispatch, refetchRequest]);

  if (!isRevampEnabled) {
    return null;
  }

  return (
    <PlanRevampProvider>
      <Box
        h='100%'
        display='flex'
        flexDirection='column'
        overflow='hidden'
        bg='white'
        borderRight='1px solid'
        borderColor='#D9D9D9'
        p={PLAN_INPUT_PANEL_PADDING}
        gap={PLAN_INPUT_PANEL_GAP}
        boxSizing='border-box'
      >
        <DriverPanelHeader
          showViewToggle
          viewMode={isPropertyView ? 'property' : 'table'}
          onViewModeChange={(mode) => onUpdateOrgType(mode === 'property' ? 2 : 1)}
          onClose={onClose}
        />
        <Box flex='1' minH={0} overflow='hidden' display='flex' flexDirection='column'>
          {isPropertyView ? (
            <Box flex='1' minH={0} overflow='hidden'>
              <RevampDesktopPanel
                orgType
                longClickCheck={longClickCheck}
                isLongClicked={isLongClicked}
                refetchRequest={onInputEdit}
              />
            </Box>
          ) : (
            <RevampDriverTableView refetchRequest={onInputEdit} />
          )}
        </Box>
      </Box>
    </PlanRevampProvider>
  );
};

export default memo(RevampOrganisationPanel);
